import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Form, Input, Space, notification, Typography, Row, Col, Select, Switch, Spin, Radio, Tooltip, Alert } from 'antd';
import { ArrowLeftOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getDocumentTemplateDetails, updateDocumentTemplate } from '../../actions/documentTemplateActions';
import { DOCUMENT_TEMPLATE_UPDATE_RESET } from '../../constants/documentTemplateConstants';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;
const { TextArea } = Input;

const SPECIALTIES = [
  'Cardiology',
  'Dermatology',
  'Emergency Medicine',
  'Endocrinology',
  'Family Medicine',
  'Gastroenterology',
  'General Surgery',
  'Hematology',
  'Internal Medicine',
  'Neurology',
  'Obstetrics and Gynecology',
  'Oncology',
  'Ophthalmology',
  'Orthopedics',
  'Pediatrics',
  'Psychiatry',
  'Pulmonology',
  'Radiology',
  'Rheumatology',
  'Urology'
];

const DocumentTemplateUpdateScreen = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const [contentType, setContentType] = useState('raw'); // 'raw' or 'html'
  const [baseTemplateId, setBaseTemplateId] = useState(null);

  const documentTemplateDetails = useSelector(state => state.documentTemplateDetails);
  const { loading: loadingDetails, error: errorDetails, documentTemplate } = documentTemplateDetails;

  const documentTemplateUpdate = useSelector(state => state.documentTemplateUpdate);
  const { loading: loadingUpdate, error: errorUpdate, success: successUpdate } = documentTemplateUpdate;

  useEffect(() => {
    if (successUpdate) {
      api.success({
        message: t('Success'),
        description: t('Template updated successfully'),
        placement: 'bottom'
      });
      dispatch({ type: DOCUMENT_TEMPLATE_UPDATE_RESET });
      // Refresh the template details
      dispatch(getDocumentTemplateDetails(id));
    } else {
      if (!documentTemplate || documentTemplate.id !== id) {
        dispatch(getDocumentTemplateDetails(id));
      } else {
        form.setFieldsValue({
          ...documentTemplate,
          tags: documentTemplate.tags ? documentTemplate.tags.join(', ') : ''
        });
        setBaseTemplateId(documentTemplate.baseTemplateId);
        // Set content type based on which field has content
        setContentType(documentTemplate.rawContent ? 'raw' : 'html');
      }
    }
    if (errorUpdate) {
      api.error({
        message: t('Error'),
        description: errorUpdate,
        placement: 'bottom'
      });
      dispatch({ type: DOCUMENT_TEMPLATE_UPDATE_RESET });
    }
  }, [dispatch, id, documentTemplate, successUpdate, form, navigate, t, errorUpdate]);

  const onFinish = (values) => {
    // Pass id as first parameter and changes as second parameter
    dispatch(updateDocumentTemplate(id, {
      ...values,
      baseTemplateId: baseTemplateId,
      tags: values.tags ? values.tags.split(',').map(tag => tag.trim()) : [],
      updatedAt: new Date().toISOString()
    }));
  };

  return (
    <Row gutter={[16, 16]} style={{ padding: "16px" }}>
      <Col xs={24}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate(-1)}
          >
            {t('Return back')}
          </Button>
          <Title level={2}>{t('Update Document Template')}</Title>
        </Space>
      </Col>

      <Col xs={24} md={16} lg={12}>
        {contextHolder}
        <Card>
          {loadingDetails ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
            </div>
          ) : errorDetails ? (
            <div style={{ color: 'red' }}>{errorDetails}</div>
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              size="large"
            >
              <Form.Item
                label={
                  <Space>
                    {t('Name')}
                    <Tooltip title={t('The name of the template that will be displayed in the list')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="name"
                rules={[{ required: true, message: t('Name is required') }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    {t('Language')}
                    <Tooltip title={t('The primary language of this template')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="language"
                rules={[{ required: true, message: t('Language is required') }]}
              >
                <Select>
                  <Select.Option value="en">English</Select.Option>
                  <Select.Option value="fr">French</Select.Option>
                  <Select.Option value="es">Spanish</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    {t('Specialty')}
                    <Tooltip title={t('The medical specialty this template is designed for')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="specialty"
                rules={[{ required: true, message: t('Specialty is required') }]}
              >
                <Select
                  showSearch
                  placeholder="Select a specialty"
                  optionFilterProp="children"
                >
                  {SPECIALTIES.map(specialty => (
                    <Select.Option key={specialty} value={specialty}>
                      {specialty}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    {t('Content Type')}
                    <Tooltip title={t('Choose between raw text or HTML formatted content')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
              >
                <Radio.Group value={contentType} onChange={e => setContentType(e.target.value)}>
                  <Radio.Button value="raw">Raw Content</Radio.Button>
                  <Radio.Button value="html">HTML Content</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {contentType === 'raw' && (
                <Form.Item
                  label={
                    <Space>
                      {t('Raw Content')}
                      <Tooltip title={t('The template content in raw text format')}>
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name="rawContent"
                >
                  <TextArea rows={8} />
                </Form.Item>
              )}

              {contentType === 'html' && (
                <Form.Item
                  label={
                    <Space>
                      {t('HTML Content')}
                      <Tooltip title={t('The template content in HTML format')}>
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name="htmlContent"
                >
                  <TextArea rows={6} />
                </Form.Item>
              )}

              <Form.Item
                label={
                  <Space>
                    {t('Structure')}
                    <Tooltip title={t('The structural format or schema of the template')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="structure"
              >
                <TextArea rows={4} />
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    {t('Example Note')}
                    <Tooltip title={t('An example of how this template should be used')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="exampleNote"
              >
                <TextArea rows={4} />
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    {t('How To Use')}
                    <Tooltip title={t('Instructions for using this template')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="howToUse"
              >
                <TextArea rows={4} />
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    {t('Tags')}
                    <Tooltip title={t('Comma-separated tags to help categorize and find this template')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="tags"
              >
                <Input placeholder="tag1, tag2, tag3" />
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    {t('Is Public')}
                    <Tooltip title={t('Make this template available to all users')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="isPublic"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>

              <Form.Item>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={loadingUpdate}>
                      {t('Update')}
                    </Button>
                    <Button onClick={() => navigate(-1)}>
                      {t('Cancel')}
                    </Button>
                  </Space>
                  {successUpdate && (
                    <Alert
                      message={t('Success')}
                      description={t('Template updated successfully')}
                      type="success"
                      showIcon
                    />
                  )}
                  {errorUpdate && (
                    <Alert
                      message={t('Error')}
                      description={errorUpdate}
                      type="error"
                      showIcon
                    />
                  )}
                </Space>
              </Form.Item>
            </Form>
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default DocumentTemplateUpdateScreen; 